import { BundleVariantGroup } from './types';

export const computeVariantGroupsTotal = (
  groups: BundleVariantGroup[]
): number =>
  groups.reduce(
    (sum, group) =>
      sum +
      group.options.reduce(
        (groupSum, option) =>
          groupSum + (Number(option.price) || 0),
        0
      ),
    0
  );

export const getValidOptionCount = (group: BundleVariantGroup): number =>
  group.options.filter((option) => option.labelEn?.trim()).length;

const getGroupLabel = (group: BundleVariantGroup): string =>
  group.labelEn?.trim() || 'this variant group';

export const validateVariantGroup = (group: BundleVariantGroup): string | null => {
  const groupName = getGroupLabel(group);
  const validCount = getValidOptionCount(group);
  const rowCount = group.options.length;

  if (group.selectionType === 'multiple') {
    const maxError = getMaxSelectionFieldError(group);
    if (maxError) {
      return `"${groupName}" — ${maxError}`;
    }
  }

  if (!group.isRequired) {
    return null;
  }

  if (rowCount === 0) {
    return `"${groupName}" is Required — add at least 1 product for customers to choose from.`;
  }

  if (validCount === 0) {
    return `"${groupName}" is Required — enter at least 1 product name.`;
  }

  return null;
};

/** Field-level error for Maximum Selection (use as helperText / schema message). */
export const getMaxSelectionFieldError = (
  group: BundleVariantGroup
): string | null => {
  if (group.selectionType !== 'multiple') return null;

  const max = Number(group.maxSelections);
  const rowCount = group.options.length;

  if (!Number.isFinite(max) || max < 1) {
    return 'Enter a Maximum Selection Limit (at least 1).';
  }
  if (rowCount === 0) {
    return 'Add products before setting Maximum Selection Limit.';
  }
  if (max > rowCount) {
    return `Maximum Selection Limit cannot be more than ${rowCount} products.`;
  }
  return null;
};

export const validateVariantGroups = (
  groups: BundleVariantGroup[]
): string | null => {
  for (const group of groups) {
    const error = validateVariantGroup(group);
    if (error) return error;
  }
  return null;
};

export const getVariantRequirementHint = (group: BundleVariantGroup): string => {
  const max = Number(group.maxSelections);

  if (!group.isRequired) {
    if (group.selectionType === 'single') {
      return 'Optional · Customer can skip or pick exactly 1 product';
    }
    if (Number.isFinite(max) && max > 0) {
      return `Optional · Customer can skip or pick up to ${max} products`;
    }
    return 'Optional · Customer can skip or pick any products';
  }

  if (group.selectionType === 'single') {
    return 'Required · Customer must pick exactly 1 product';
  }

  if (Number.isFinite(max) && max > 0) {
    return `Required · Customer must pick 1 to ${max} products`;
  }

  return 'Required · Customer must pick at least 1 product';
};
